/**
 * WikiConnectorsSection — the "Wiki connectors" section of the agent editor's
 * Connectors tab: the knowledge wikis THIS agent may search and cite.
 *
 * Unlike the MCP and API sections, a wiki binding is not its own record — it
 * rides the agent form as `handler_config.wikis` and is saved with the rest
 * of the form by the tab's Save button. Which wikis CAN be chosen is decided
 * elsewhere: a super admin registers a wiki and connects it to the
 * organisation (`GET /wiki/options` lists those). Each binding may carry its
 * own usage instructions, overriding the wiki's default.
 *
 * It replaced the editor's separate "Integrations" tab (operator request
 * 2026-09-28): a wiki is one more outside source the agent reaches, so it
 * belongs beside Drive, MCP servers and APIs, in the same idiom.
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import api from '@services/api';
import { ExpandableTextarea } from '@/components/common';
import { ConnectorDialog, PlusGlyph, SectionHead } from './ConnectorDialog';

/** One wiki bound to the agent, as stored in `handler_config.wikis`. */
export interface WikiIntegration {
  wiki_id: string;
  instructions_override?: string | null;
}

interface WikiOption {
  wiki_id: string;
  name: string;
  slug: string;
}

interface Props {
  value: WikiIntegration[];
  onChange: (next: WikiIntegration[]) => void;
  /** Writes the agent's bindings now, without waiting for the tab's Save. */
  onPersist: () => Promise<void>;
  disabled?: boolean;
}

export default function WikiConnectorsSection({ value, onChange, onPersist, disabled }: Props) {
  const { t } = useLanguage();
  const [options, setOptions] = useState<WikiOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // The wikis connected to this organisation — the only ones that can be chosen.
  useEffect(() => {
    let cancelled = false;
    api.get('/wiki/options')
      .then((res) => { if (!cancelled) setOptions(res.data?.wikis || []); })
      .catch(() => { if (!cancelled) setOptions([]); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const optionFor = (id: string) => options.find((w) => w.wiki_id === id);
  const available = options.filter((w) => !value.some((i) => i.wiki_id === w.wiki_id));

  const add = (wiki_id: string) => {
    if (!wiki_id || value.some((i) => i.wiki_id === wiki_id)) return;
    onChange([...value, { wiki_id, instructions_override: null }]);
    setPickerOpen(false);
  };
  const remove = (wiki_id: string) => onChange(value.filter((i) => i.wiki_id !== wiki_id));
  const setInstructions = (wiki_id: string, val: string) =>
    onChange(value.map((i) => (i.wiki_id === wiki_id ? { ...i, instructions_override: val || null } : i)));

  const persist = async () => {
    setSaving(true);
    try {
      await onPersist();
    } finally {
      setSaving(false);
    }
  };

  const nothingToAdd = loaded && available.length === 0;

  return (
    <section className="cxs" data-testid="wiki-connectors-section">
      <SectionHead
        icon={<BookGlyph />}
        title={t('bots.connectors.wiki.title')}
        description={t('bots.connectors.wiki.description')}
        action={(
          <button type="button" className="cx-btn cx-btn--primary" disabled={disabled || nothingToAdd}
                  title={nothingToAdd && options.length > 0 ? t('wiki.integrations.allAdded') : undefined}
                  onClick={() => setPickerOpen(true)} data-testid="wiki-add-connector">
            <PlusGlyph />
            {t('bots.connectors.wiki.add')}
          </button>
        )}
      />

      {loaded && options.length === 0 && (
        <p className="cxs__note" data-testid="wiki-none-connected-note">{t('wiki.noneConnected')}</p>
      )}
      {loaded && options.length > 0 && value.length === 0 && (
        <p className="cxs__note" data-testid="wiki-empty-note">{t('bots.connectors.wiki.empty')}</p>
      )}

      {value.length > 0 && (
        <div className="cxs__list">
          {value.map((integ) => {
            const option = optionFor(integ.wiki_id);
            const expanded = expandedId === integ.wiki_id;
            const testSlug = option?.slug ?? integ.wiki_id;
            return (
              <div key={integ.wiki_id} className={`cxc cxc--column is-on ${disabled ? 'is-disabled' : ''}`}
                   data-testid={`wiki-connector-${testSlug}`}>
                <div className="cxc__row">
                  <span className="cxc__icon" aria-hidden="true"><BookGlyph /></span>
                  <span className="cxc__body">
                    <span className="cxc__name" dir="auto">{option?.name ?? integ.wiki_id}</span>
                    {option && (
                      <span className="cxc__meta">
                        <span className="cxc__url" dir="ltr">{option.slug}</span>
                      </span>
                    )}
                    <span className="cxc__pills">
                      <span className="cx-pill cx-pill--ok" data-testid="wiki-status-ok">
                        {t('bots.connectors.mcp.statusConnected')}
                      </span>
                      <button type="button" className="cxc__link"
                              onClick={() => setExpandedId(expanded ? null : integ.wiki_id)}
                              aria-expanded={expanded} data-testid={`wiki-instructions-toggle-${testSlug}`}>
                        {t('wiki.instructions')}
                      </button>
                    </span>
                  </span>
                  <span className="cxc__end">
                    <span className="cxc__actions">
                      <button type="button" className="cx-btn cx-btn--sm cx-btn--danger" disabled={disabled}
                              onClick={() => remove(integ.wiki_id)} data-testid={`wiki-remove-${testSlug}`}>
                        {t('common.remove')}
                      </button>
                    </span>
                  </span>
                </div>

                {expanded && (
                  <div className="cxc__detail" data-testid={`wiki-instructions-${testSlug}`}>
                    <div className="cxc__detail-head">
                      <p className="cxc__detail-label">{t('wiki.instructions')}</p>
                      <button type="button" className="cx-btn cx-btn--sm" disabled={disabled || !integ.instructions_override}
                              onClick={() => setInstructions(integ.wiki_id, '')}
                              title={t('wiki.resetInstructionsTitle')}>
                        {t('wiki.resetInstructions')}
                      </button>
                    </div>
                    <p className="cxc__detail-hint">{t('wiki.instructionsHelp')}</p>
                    <ExpandableTextarea
                      value={integ.instructions_override || ''}
                      onChange={(val) => setInstructions(integ.wiki_id, val)}
                      placeholder={t('wiki.instructionsPlaceholder')}
                      disabled={disabled}
                      rows={6}
                      label={t('wiki.instructions')}
                    />
                    <div className="cxc__detail-actions">
                      <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" disabled={disabled || saving}
                              onClick={() => { void persist(); }} data-testid={`wiki-save-${testSlug}`}>
                        {saving ? t('common.saving') : t('common.save')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pickerOpen && (
        <ConnectorDialog
          size="medium"
          icon={<BookGlyph />}
          title={t('wiki.integrations.pickWiki')}
          subtitle={t('bots.connectors.wiki.description')}
          onClose={() => setPickerOpen(false)}
          testId="wiki-pick-modal"
          actions={(
            <button type="button" className="btn-cancel" onClick={() => setPickerOpen(false)}>{t('common.cancel')}</button>
          )}
        >
          {available.length === 0 ? (
            <p className="cxd__empty">{t('wiki.integrations.allAdded')}</p>
          ) : (
            <div className="cx-choices">
              {available.map((w) => (
                <button key={w.wiki_id} type="button" className="cx-choice" onClick={() => add(w.wiki_id)}
                        data-testid={`wiki-pick-${w.slug}`}>
                  <span className="cxc__icon" aria-hidden="true"><BookGlyph /></span>
                  <span className="cxc__body">
                    <span className="cxc__name" dir="auto">{w.name}</span>
                    <span className="cxc__meta"><span className="cxc__url" dir="ltr">{w.slug}</span></span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </ConnectorDialog>
      )}
    </section>
  );
}

function BookGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
         strokeLinejoin="round" aria-hidden="true">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}
